/**
 * ShortcutTipCard
 *
 * One-time tip that teaches users about the keyboard-shortcut cheat sheet.
 * Slides in from the bottom-right after a short delay, persists dismissal in
 * localStorage so it never shows again. Users can dismiss it explicitly ("Got
 * it"), click "Show shortcuts" to open the cheat-sheet modal directly, or
 * wait — it auto-dismisses after 15 seconds.
 *
 * Strictly flat styling: no shadows, no gradients; just a muted card with a
 * subtle border, in line with the app styleguide.
 */

import React from 'react';
import { Keyboard, X } from 'lucide-react';

import { cn } from '../utils/cn';
import { getKeyDisplayText, getModifierKey } from '../utils/platform';

const STORAGE_KEY = 'shortcut-tip-card:dismissed';
/** Delay before showing, so the card doesn't clash with initial loaders. */
const SHOW_DELAY_MS = 2500;
/** Auto-dismiss after this long if the user never interacts. */
const AUTO_DISMISS_MS = 15_000;

const Kbd: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <kbd
    className={cn(
      'inline-flex items-center justify-center',
      'min-w-[22px] h-5 px-1.5',
      'rounded-[5px] border border-border/60',
      'bg-muted/60 text-foreground',
      'text-[10px] font-medium font-mono'
    )}
  >
    {children}
  </kbd>
);

export type ShortcutTipCardProps = {
  /** Called when the user clicks "Show shortcuts" — should open the modal. */
  onOpenCheatSheet: () => void;
};

export const ShortcutTipCard: React.FC<ShortcutTipCardProps> = ({ onOpenCheatSheet }) => {
  const [isVisible, setIsVisible] = React.useState(false);
  const [isExiting, setIsExiting] = React.useState(false);

  // Check dismissed flag + schedule show on mount
  React.useEffect(() => {
    if (typeof window === 'undefined') return;

    let dismissed = false;
    try {
      dismissed = window.localStorage.getItem(STORAGE_KEY) === '1';
    } catch {
      // localStorage disabled — treat as not-dismissed, but don't persist
    }
    if (dismissed) return;

    const showTimer = window.setTimeout(() => setIsVisible(true), SHOW_DELAY_MS);
    return () => window.clearTimeout(showTimer);
  }, []);

  // Auto-dismiss timer — only runs while visible
  React.useEffect(() => {
    if (!isVisible || isExiting) return;
    const autoTimer = window.setTimeout(() => handleDismiss(), AUTO_DISMISS_MS);
    return () => window.clearTimeout(autoTimer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible, isExiting]);

  const persistDismissed = React.useCallback(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, '1');
    } catch {
      // localStorage disabled — silently no-op
    }
  }, []);

  const handleDismiss = React.useCallback(() => {
    setIsExiting(true);
    persistDismissed();
    // Let the exit animation finish before unmounting
    window.setTimeout(() => setIsVisible(false), 200);
  }, [persistDismissed]);

  const handleShowCheatSheet = React.useCallback(() => {
    persistDismissed();
    setIsExiting(true);
    window.setTimeout(() => {
      setIsVisible(false);
      onOpenCheatSheet();
    }, 150);
  }, [onOpenCheatSheet, persistDismissed]);

  if (!isVisible) return null;

  const modifier = getKeyDisplayText(getModifierKey());

  return (
    <div
      role="status"
      aria-live="polite"
      className={cn(
        'fixed bottom-4 right-4 z-[60] pointer-events-auto',
        'w-[280px] max-w-[calc(100vw-32px)]',
        'rounded-xl border border-border/60 bg-card',
        'p-3.5',
        'transition-all duration-200 ease-out',
        isExiting
          ? 'opacity-0 translate-y-2'
          : 'opacity-100 translate-y-0 animate-in slide-in-from-bottom-2 fade-in'
      )}
    >
      {/* Close button */}
      <button
        type="button"
        onClick={handleDismiss}
        className={cn(
          'absolute top-2 right-2 p-1 rounded',
          'text-muted-foreground/60 hover:text-foreground hover:bg-muted/60',
          'transition-colors'
        )}
        aria-label="Dismiss tip"
      >
        <X className="w-3.5 h-3.5" />
      </button>

      {/* Header */}
      <div className="flex items-start gap-2.5 mb-2 pr-5">
        <div className="flex-shrink-0 mt-0.5">
          <div className="w-7 h-7 rounded-md bg-primary/10 flex items-center justify-center">
            <Keyboard className="w-3.5 h-3.5 text-primary-text" />
          </div>
        </div>
        <div className="min-w-0">
          <div className="text-[13px] font-semibold text-foreground leading-snug">
            Keyboard shortcuts
          </div>
          <div className="text-[11px] text-muted-foreground mt-0.5">
            Speed things up
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="text-[12px] text-muted-foreground leading-relaxed mb-3">
        Press <Kbd>{modifier}</Kbd> <Kbd>/</Kbd> anywhere to see every shortcut available in the app.
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={handleShowCheatSheet}
          className={cn(
            'flex-1 px-3 py-1.5 rounded-md text-[12px] font-medium',
            'bg-primary text-primary-foreground',
            'hover:bg-primary/90 transition-colors'
          )}
        >
          Show shortcuts
        </button>
        <button
          type="button"
          onClick={handleDismiss}
          className={cn(
            'px-3 py-1.5 rounded-md text-[12px] font-medium',
            'text-muted-foreground hover:text-foreground hover:bg-muted/60',
            'transition-colors'
          )}
        >
          Got it
        </button>
      </div>
    </div>
  );
};

ShortcutTipCard.displayName = 'ShortcutTipCard';
